<template>
  <div>
    <slot />
  </div>
</template>
<script>
import debounce from 'lodash.debounce'
import { formatISO, add, getUnixTime, parseISO } from 'date-fns'

import { saveEventPosition } from '~/queries/saveEventState'
import setEventTimer from '~/queries/setEventTimer'

const PREV = 'prev'
const NEXT = 'next'

export default {
  name: 'SlideshowComponent',
  props: {
    startStep: { default: 1 },
    lastSlide: { default: null },
    embedded: { default: false },
    inserted: { default: false },
    keyboardNavigation: { default: true },
    mouseNavigation: { default: true },
    skip: { default: false },
    backBySlide: { default: false },
    repeat: { default: false },
    blockAdvance: { default: false },
    blockAdvanceMessage: { default: 'Slide advance is blocked!' }
  },
  data () {
    return {
      currentSlideIndex: 1,
      isSlideshow: true,
      step: 1,
      playerTurnIndex: 0,
      active: true,
      secondsLeft: false,
      timerPausedAt: null,
      slideshowMounted: false,
      direction: NEXT,
      debouncedSavePosition: '',
      lastPositionChange: null // most recent slide advance, used to compute answer time,
    }
  },
  computed: {
    currentSlideComponent () {
      // console.log(this.$children)
      return this.$children.find(child => child.active && child.isSlide)
    },
    currentSlideTemplate () {
      return this.currentSlideComponent?.$children[0]
    },
    currentSlide () {
      return this.slides[this.currentSlideIndex - 1]
    },
    currentSlideSteps () {
      return this.$store.state.currentSteps
    },
    // multi-choice gameshow mode
    showPreviewAnswersThisSlide () {
      /// if current step is not one return
      if (this.step !== 1) { return false }
      if (this.currentSlide.template !== 'multipleChoice') { return false }
      return this.currentSlideTemplate?.options?.previewAnswers === true
    },
    nextSlideIndex () {
      let nextSlideIndex = this.currentSlideIndex + 1

      // increment next index until we find a slide that isn't marked skip
      while ((nextSlideIndex < this.slides.length + 1) &&
             (this.slides[nextSlideIndex - 1].options.skip)) {
        nextSlideIndex++
      }
      return nextSlideIndex
    },
    prevSlideIndex () {
      let prevSlideIndex = this.currentSlideIndex - 1

      // decrement until we find a slide that isn't marked skip
      while ((prevSlideIndex >= 1) &&
             (this.slides[prevSlideIndex - 1].options.skip)) {
        prevSlideIndex--
      }
      return prevSlideIndex
    },
    nextStepNum () {
      // this.step is 1 indexed, the array is 0 indexed so the current step is the next array key
      const nextStepKey = this.step
      const steps = this.currentSlideSteps
      if (!steps) { return false }

      const next = steps.findIndex((step, index) => {
        return step.skip !== true && index >= nextStepKey
      })

      return next > -1 ? next + 1 : false
    },
    prevStepNum () {
      // this.step is 1 indexed, the array is 0 indexed so the previous step key is two less than the current step
      const prevStepKey = this.step - 2
      const steps = this.currentSlideSteps
      if (!steps) { return false }

      let prev = false

      // iterate backwards through the steps array to find the most recent step that matches
      const len = steps.length
      for (let i = len; i >= 0; i--) {
        if (steps[i] && steps[i].skip !== true && i <= prevStepKey) {
          prev = i + 1
          break
        }
      }

      return prev
    },
    isModerator () {
      const hostJoinedAsPlayer = this.$store.state.hostJoinedAsPlayer
      return !hostJoinedAsPlayer && this.event && this.$store.state.auth.loggedIn &&
             this.$store.state.auth.user.id === this.event.primaryUser
    },
    isLocal () {
      return this.isLocalPreview
    },
    slides () {
      return this.slideDeck.slides || []
    },
    spectatorEventId () {
      return this.eventMeta?.settings?.spectatorEventId
    },
    isSpectatorEvent () {
      return this.eventMeta?.settings?.isSpectatorEvent === true
    },
    shouldSyncSpectatorEvent () {
      return this.spectatorEventId &&
             this.eventMeta?.settings?.isMainEvent === true
    }
  },
  watch: {
    currentSlideIndex (index) {
      if (this.isModerator) { this.debouncedSavePosition() }
    },
    step (val) {
      this.lastPositionChange = Date.now()
      if (this.isModerator) { this.debouncedSavePosition() }
    },
    playerTurnIndex (val) {
      if (this.isModerator) { this.debouncedSavePosition() }
    },
    active: 'updateSlideshowVisibility',
    computedActive: 'updateSlideshowVisibility'
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.handleKeydown)

    const element = this.$el
    element.removeEventListener('click', this.handleClick)
    element.removeEventListener('touchstart', this.handleClick)
    clearInterval(this.timerInterval)
  },
  mounted () {
    this.debouncedSavePosition = debounce(this.savePosition, 100)
  },
  methods: {
    isActive (key) {
      return key === (this.currentSlideIndex - 1)
    },
    savePosition () {
      this.log('save event position')
      if (!this.isModerator || this.isLocal || this.isLocalPreview || this.isRecap) {
        this.log('not saving, is moderator, local, or recap')
        return
      }

      if (this.isSpectatorEvent) {
        this.log('spectator event, not saving position')
        return
      }

      const position = {
        currentSlide: this.currentSlideIndex,
        step: this.step,
        playerTurnIndex: this.playerTurnIndex
      }

      const eventId = this.eventId

      const variables = {
        eventId,
        position,
        currentSlide: position.currentSlide,
        now: new Date().toISOString()
      }

      this.$apollo.mutate({
        mutation: saveEventPosition,
        variables
      })

      if (this.shouldSyncSpectatorEvent) {
        this.saveSpectatorEventPosition(variables)
      }

      this.trackPositionChange()
    },
    saveSpectatorEventPosition (variables) {
      this.log('syncing spectator event position')
      variables.eventId = this.spectatorEventId

      this.$apollo.mutate({
        mutation: saveEventPosition,
        variables
      })
    },
    onStartExit () {
      this.$emit('onStartExit')
    },
    onEndExit () {
      this.$emit('onEndExit')
    },
    changeDirection (direction) {
      this.direction = direction
      this.$root.direction = direction
    },
    findStartingStep (steps) {
      // console.log('finding starting step for:', steps)
      if (!steps[0] || !steps[0].name) { return 1 }
      const startStepIndex = steps.findIndex((step) => { return step.skip !== true })
      // console.log('first step is: ' + (startStepIndex + 1))
      return startStepIndex + 1 // move from 0 indexing to 1 indexing
    },
    async nextStep () {
      if (this.blockAdvance) {
        this.$buefy.toast.open({ type: 'is-primary', message: this.blockAdvanceMessage })
        this.log('blocking slide advance via prop')
        return
      }

      if (this.isSpectatorEvent) { this.blockSpectatorSlideChange(); return }

      // check for multi-choice gameshow mode
      if (this.showPreviewAnswersThisSlide) {
        const showingPreviewAnswers = this.currentSlideTemplate?.showPreviewAnswers
        if (!showingPreviewAnswers) {
          this.currentSlideTemplate.showPreviewAnswers = true
          return
        }
      }

      // console.log('next step')
      this.changeDirection(NEXT)
      await this.clearTimer()
      this.$nextTick(() => {
        if (!this.nextStepNum) {
          this.nextSlide()
        } else {
          this.step = this.nextStepNum
        }
      })
    },
    async previousStep () {
      if (this.isSpectatorEvent) { this.blockSpectatorSlideChange(); return }

      // check for multi-choice gameshow mode
      if (this.showPreviewAnswersThisSlide) {
        const showingPreviewAnswers = this.currentSlideTemplate?.showPreviewAnswers
        if (showingPreviewAnswers) {
          this.currentSlideTemplate.showPreviewAnswers = false
          return
        }
      }

      // console.log('previous step')
      this.changeDirection(PREV)
      await this.clearTimer()
      this.$nextTick(() => {
        if (!this.prevStepNum) {
          this.previousSlide()
        } else {
          this.step = this.prevStepNum
        }
      })
    },
    nextSlide () {
      if (this.isSpectatorEvent) { this.blockSpectatorSlideChange(); return }

      this.changeDirection(NEXT)

      const nextSlideIndex = this.nextSlideIndex

      const hasNextSlide = this.slides[nextSlideIndex - 1] !== undefined && this.slides[nextSlideIndex - 1] !== null
      const nextSlideSteps = hasNextSlide ? this.slides[nextSlideIndex - 1].steps : false
      // console.log('next slide steps: ', nextSlideSteps)

      if (hasNextSlide) {
        this.currentSlideIndex = nextSlideIndex
        this.step = this.findStartingStep(nextSlideSteps)
      }

      this.$emit('slideChange', this.currentSlideIndex)
      // } else if (this.repeat) {
      //   this.currentSlideIndex = 1
      // } else if (!this.embedded) {
      //   this.onEndExit()
      // }
    },
    previousSlide () {
      if (this.isSpectatorEvent) { this.blockSpectatorSlideChange(); return }

      this.changeDirection(PREV)

      const prevSlideIndex = this.prevSlideIndex

      const hasPrevSlide = this.slides[prevSlideIndex - 1] !== undefined && this.slides[prevSlideIndex - 1] !== null
      const prevSlideSteps = hasPrevSlide ? this.slides[prevSlideIndex - 1].steps : false
      // console.log('prev slide steps: ', this.slideDeck.slides[prevSlideIndex - 1])

      if (hasPrevSlide) {
        this.currentSlideIndex = prevSlideIndex
        this.step = this.findStartingStep(prevSlideSteps)
        this.$emit('slideChange', this.currentSlideIndex)
      } else if (!this.embedded) {
        this.onStartExit()
      }
    },
    trackPositionChange () {
      // only track live events and changes from host account
      if (this.event?.id && this.event?.shortcode && this.slideDeck) {
        this.posthogCapture('event:' + this.direction + '_position', {
          event_id: this.event.id,
          shortcode: this.event.shortcode,
          deck_id: this.slideDeck?.id,
          slide: this.currentSlideIndex,
          step: this.step,
          player_turn: this.playerTurnIndex
        })
      }
    },
    goToSlide (slideIndex) {
      this.changeDirection(NEXT)
      this.currentSlideIndex = slideIndex

      if (this.slides[slideIndex]) {
        const slideSteps = this.slides[slideIndex].steps
        this.step = this.findStartingStep(slideSteps)
      }
    },
    goToStep (num) {
      this.step = num
    },
    getClientTime () {
      const clientTimeOffset = this.$store.state.clientTimeOffset
      const serverTime = Date.now() + clientTimeOffset
      return new Date(serverTime)
    },
    blockSpectatorSlideChange () {
      if (!this.isModerator) { return }

      this.snackbar = this.$buefy.snackbar.open({
        message: 'Return to the main event to change position?',
        actionText: 'Click to return',
        duration: 3500,
        type: 'is-success',
        onAction: () => {
          this.$router.push('/event/' + this.$route.params.id)
        }

      })
    },
    async setEventTimer (timerEnd) {
      const mutation = setEventTimer
      const variables = {
        eventId: this.eventId,
        timerEnd
      }
      await this.$apollo.mutate({ mutation, variables })

      if (this.shouldSyncSpectatorEvent) {
        variables.eventId = this.spectatorEventId
        await this.$apollo.mutate({ mutation, variables })
      }
    },
    startTimer (seconds) {
      this.log('starting timer for ' + seconds + ' seconds')
      if (!this.isModerator || this.isRecap || this.isSpectatorEvent) { return }

      const timerEnd = formatISO(add(this.getClientTime(), { seconds }))
      this.event.timerEnd = timerEnd
      this.secondsLeft = seconds

      this.setEventTimer(timerEnd)
    },
    refreshTimer () {
      if (this.isRecap) { this.secondsLeft = false; return }

      if (!this.event.timerEnd) {
        this.secondsLeft = false
      } else {
        const secondsLeft = getUnixTime(parseISO(this.event.timerEnd)) - getUnixTime(this.getClientTime())

        if (secondsLeft >= 0) {
          this.secondsLeft = secondsLeft
        } else {
          this.secondsLeft = false
          this.event.timerEnd = null
          this.nextStep()
        }
      }
    },
    async clearTimer (clearPause = true) {
      if (!this.isModerator || this.isRecap) { return }

      if (clearPause) { this.timerPausedAt = null }
      this.event.timerEnd = null
      this.secondsLeft = false
      await this.setEventTimer(null)
    },
    toggleTimer () {
      if (!this.isModerator) { return }
      this.log('toggling timer in slideshow component')
      if (this.timerPausedAt) {
        this.startTimer(this.timerPausedAt)
        this.timerPausedAt = null
      } else {
        this.pauseTimer()
      }
    },
    pauseTimer () {
      if (!this.isModerator) { return }
      this.timerPausedAt = this.secondsLeft
      this.clearTimer(false)
    },
    handleClick (event) {
      if (!this.isModerator) { return }
      const noHref = event.target.href === undefined
      const slideshow = document.getElementById('current-slide')
      if (!slideshow) {
        console.warn('handleClick: no slideshow found in DOM (#current-slide)')
        return
      }
      const slideBox = slideshow.getBoundingClientRect()

      // this.log('handling click, slideshow rectangle: ', slideBox)

      if (this.mouseNavigation && noHref && !event.altKey) {
        const clientX = event.clientX != null ? event.clientX : event.touches[0].clientX
        const clientY = event.clientY != null ? event.clientY : event.touches[0].clientY

        if (clientY < slideBox.top || clientY > slideBox.bottom) { return }
        if (clientX < slideBox.left || clientX > slideBox.right) { return }

        if (clientX < slideBox.left + (0.25 * slideBox.width)) {
          event.preventDefault()
          this.previousStep()
        } else if (clientX > slideBox.right - (0.4 * slideBox.width)) {
          event.preventDefault()
          this.nextStep()
        }
      }
    },
    handleKeydown (evt) {
      if (this.keyboardNavigation) {
        if (evt.key === 'ArrowLeft' || evt.key === 'PageUp') {
          this.previousStep()
          evt.preventDefault()
        } else if (evt.key === 'ArrowRight' || evt.key === 'PageDown') {
          this.nextStep()
          evt.preventDefault()
        }
      }
    },
    afterMounted () {
      // useful in some instances
    },
    mountSlideshow () {
      this.log('mounting slideshow')

      // LIST ALL SLIDES
      this.isSlideshow = true
      this.slideshowMounted = true

      // console.log('found slides', this.slides)

      if (this.isModerator) {
        // ADD NAVIGATION EVENTS
        // console.log('adding event listeners')

        const element = this.$el

        if (this.keyboardNavigation) {
          window.addEventListener('keydown', this.handleKeydown)
        }
        if (this.mouseNavigation) {
          if ('ontouchstart' in window) {
            element.addEventListener('touchstart', this.handleClick)
          } else {
            element.addEventListener('click', this.handleClick)
          }
        }
        if (this.embedded) {
          this.$el.className += ' embedded-slideshow'
        }
      }

      // PRELOAD PICTURES IF ANY
      if (this.preloadedImages) {
        setTimeout(() => {
          for (const image in this.preloadedImages) {
            (new Image()).src = this.preloadedImages[image]
          }
        }, 1000)
      }

      // LAST INITIALIZATIONS
      this.timerInterval = setInterval(() => {
        this.refreshTimer()
      }, 1000)
      this.afterMounted()
    }
  }
}
</script>
